// Core Modules
import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map } from 'rxjs';
import { NavigationEnd, Router, RouterLink } from '@angular/router';

// External Modules
import { NgxPermissionsService } from 'ngx-permissions';

// Models
import { NavGroup, NavItem, NAV_GROUPS } from './nav.model';

@Component({
  selector: 'app-side-nav',
  templateUrl: './side-nav.component.html',
  styleUrl: './side-nav.component.scss',
  imports: [RouterLink],
  host: { '[class.mobile-open]': 'mobileOpen()' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SideNavComponent {
  private readonly permissionsService = inject(NgxPermissionsService);
  private readonly router = inject(Router);

  /** Whether the nav is shown as an open off-canvas drawer on small screens. */
  public readonly mobileOpen = input<boolean>(false);

  /** Emits when a nav item is chosen, so the shell can close the mobile drawer. */
  public readonly navigate = output<void>();

  private readonly permissions = toSignal(this.permissionsService.permissions$, { initialValue: this.permissionsService.getPermissions() });

  /** Labels of groups the user has collapsed. */
  private readonly collapsedGroups = signal<ReadonlySet<string>>(new Set());

  /** Labelled groups reduced to the items the user may see (empty groups dropped). */
  public readonly visibleGroups = computed<NavGroup[]>(() => {
    const perms = this.permissions();
    return NAV_GROUPS.map((group) => ({ ...group, items: group.items.filter((item) => !!perms[item.permission]) })).filter((group) => group.items.length > 0);
  });

  /** Current URL path, stripped of query params/fragment, updated on every completed navigation. */
  private readonly currentPath = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => this.stripQuery(event.urlAfterRedirects))
    ),
    { initialValue: this.stripQuery(this.router.url) }
  );

  /**
   * The single nav item route considered "active" for the current path — the longest (most specific)
   * matching route among visible items, so a parent route (e.g. `/tokens`) never lights up alongside
   * a sibling that's actually the better match (e.g. `/tokens/expirations`).
   */
  private readonly activeRoute = computed<string | null>(() => {
    const path = this.currentPath();
    const matches = this.visibleGroups()
      .flatMap((group) => group.items)
      .filter((item) => path === item.route || path.startsWith(`${item.route}/`));
    return matches.reduce<string | null>((best, item) => (best === null || item.route.length > best.length ? item.route : best), null);
  });

  public isCollapsed(label: string): boolean {
    return this.collapsedGroups().has(label);
  }

  public isActive(item: NavItem): boolean {
    return item.route === this.activeRoute();
  }

  public toggleGroup(label: string): void {
    const next = new Set(this.collapsedGroups());
    if (next.has(label)) {
      next.delete(label);
    } else {
      next.add(label);
    }
    this.collapsedGroups.set(next);
  }

  private stripQuery(url: string): string {
    return url.split(/[?#]/)[0];
  }
}
